import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiPost } from '../lib/api.js';
import { interpretGuardError } from '../lib/dialGuards.js';
import TcpaQuietHoursModal from '../components/compliance/TcpaQuietHoursModal.jsx';
import CallFrequencyDialog from '../components/compliance/CallFrequencyDialog.jsx';

/**
 * Pre-dial compliance for manual dials. `guardedClickToCall` posts to
 * /phone/click-to-call and, on a quiet-hours / daily-cap 409, asks the agent and
 * retries with an explicit (audit-logged) override. Render `guardModal` once.
 * @returns {{guardedClickToCall: (payload: object) => Promise<object>,
 *   guardLead: (leadId: string) => Promise<boolean>, guardModal: JSX.Element|null}}
 */
export function useDialGuards() {
  const nav = useNavigate();
  const [pending, setPending] = useState(null);
  const ask = useCallback((kind, info) => new Promise((resolve) => setPending({ kind, info, resolve })), []);
  const settle = (ok) => { pending?.resolve(ok); setPending(null); };

  const guardedClickToCall = useCallback(async (payload) => {
    let overrides = {};
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        return await apiPost('/phone/click-to-call', { ...payload, complianceOverrides: overrides });
      } catch (err) {
        const g = interpretGuardError(err);
        if (g?.kind === 'agreement') {
          toast.error('Sign the Producer Agreement before dialing.');
          nav('/onboarding/producer-agreement?return=/phone-setup');
          return { ok: false, cancelled: true };
        }
        const key = g?.kind === 'quiet' ? 'quietHours' : g?.kind === 'frequency' ? 'frequency' : null;
        if (!key || overrides[key]) throw err;
        if (!(await ask(g.kind, g.info))) return { ok: false, cancelled: true };
        overrides = { ...overrides, [key]: true, reason: `Agent confirmed ${g.kind === 'quiet' ? 'call outside calling hours' : 'dial past daily cap'}` };
      }
    }
    return { ok: false, cancelled: true };
  }, [ask, nav]);

  /** Power Dialer single-lead kickoff: true to proceed (quiet hours only; the engine skips capped leads itself). */
  const guardLead = useCallback(async (leadId) => {
    try {
      const res = await apiPost('/phone/dial-precheck', { leadId });
      const q = res?.data?.quietHours;
      return q && !q.allowed ? ask('quiet', q) : true;
    } catch {
      return true; // never block the dialer on a failed precheck; the engine re-checks server-side
    }
  }, [ask]);

  const guardModal = pending && (pending.kind === 'quiet'
    ? <TcpaQuietHoursModal info={pending.info} onCancel={() => settle(false)} onConfirm={() => settle(true)} />
    : <CallFrequencyDialog onCancel={() => settle(false)} onConfirm={() => settle(true)} />);

  return { guardedClickToCall, guardLead, guardModal };
}

export default useDialGuards;
